import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { PersonAdd, Warning } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import AddIcon from "@mui/icons-material/Add";
import OwnerForm from "../../../components/Forms/Owner";

import { STATUS_SUCCESS } from "../../../constants/responseTypes";
import { DataTable, ColumnDef } from "../../../components/DataTable";
import StatusChip from "./components/StatusChip";
import PendingTableRow from "./components/PendingTableRow";
import DeleteOwnerDialog from "./components/DeleteOwnerDialog";
import ownerService from "../../../services/owner.service";
import { useRole } from "../../../context/RoleContext";
import { useAlert } from "../../../context/AlertContext";
import { IOwnerObject } from "../../../services/api.types";

// ---------------------------------------------------------------------------
// ONE screen for both SuperAdmin and Relationship Manager. The only
// difference between the two roles is which endpoint(s) supply the raw row
// list — that role-conditional data fetch is intentionally kept OUTSIDE the
// table and outside the column definitions. There is no role prop, no role
// branch, and no role-conditional column anywhere below this fetch.
// ---------------------------------------------------------------------------
export default function ManageOwners() {
  const { t } = useTranslation();
  const { isRelationshipManager, isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAlert } = useAlert();

  // "pending" scrolls to the pending table; "no-employee" preseeds the
  // without-employees switch; "active" preseeds the Active status filter. All filters originate from dashboard tile
  // deep-links and were previously handled separately per role.
  const incomingFilter = (location.state as { filter?: string } | null)?.filter;
  const pendingTableRef = React.useRef<HTMLDivElement>(null);

  const [owners, setOwners] = React.useState<IOwnerObject[]>([]);

  // Filters — load-bearing, must keep working for both roles (status, balance
  // range, without-employees switch, free-text search).
  const [withoutEmployees, setWithoutEmployees] = React.useState(
    incomingFilter === "no-employee"
  );
  const [statusFilter, setStatusFilter] = React.useState<
    "all" | "online" | "active" | "pending_review" | "offline"
  >(incomingFilter === "active" ? "active" : "all");
  const [search, setSearch] = React.useState("");
  const [balanceFrom, setBalanceFrom] = React.useState("");
  const [balanceTo, setBalanceTo] = React.useState("");

  // Delete-owner flow — super_admin only (route-level restrictTo on the
  // backend). Modelled on the car-wash delete flow in ManageCarWashes.tsx,
  // using the new DeleteOwnerDialog (no existing owner delete dialog).
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [ownerToDelete, setOwnerToDelete] = React.useState<IOwnerObject | null>(
    null
  );
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleClick = (id: string) => {
    navigate(`${id}/info`);
  };

  const handleDeleteClick = (owner: IOwnerObject) => {
    setOwnerToDelete(owner);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ownerToDelete?._id) return;

    setIsDeleting(true);
    try {
      await ownerService.deleteOwner(ownerToDelete._id);
      await fetchOwners();
      setDeleteModalOpen(false);
      setOwnerToDelete(null);
    } catch (error: any) {
      setAlert("error", error?.message || t("serviceErrors.deleteOwner"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setOwnerToDelete(null);
  };

  // Role-conditional DATA FETCH only. SuperAdmin's getOwners() returns every
  // owner (including pending_approval) via the legacy { status, results, data }
  // envelope; the RM-scoped getMyOwners()/getMyPendingOwners() pair already
  // splits pending from non-pending server-side but is normalized back into a
  // single raw list here so both roles run through the exact same partition,
  // filter and table logic below.
  const fetchOwners = React.useCallback(async () => {
    try {
      let raw: IOwnerObject[] = [];
      if (isRelationshipManager) {
        const [mine, pending] = await Promise.all([
          ownerService.getMyOwners(),
          ownerService.getMyPendingOwners(),
        ]);
        raw = [...(mine ?? []), ...(pending ?? [])];
      } else {
        const res = await ownerService.getOwners();
        if (res.status === STATUS_SUCCESS) {
          raw = res.data ?? [];
        }
      }
      setOwners(raw);
    } catch (err: any) {
      setAlert("error", err?.message || t("owners.failedToLoad"));
    }
  }, [isRelationshipManager, setAlert, t]);

  React.useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  // Partition owners into exactly two exhaustive buckets.
  // [FACT] The `registrationStatus` enum has four values (admin_created,
  // pending_approval, approved, rejected). This used to be a pair of NAMED
  // filters (=== "admin_created" and === "pending_approval"), so every owner
  // whose status was `approved` — which is what acceptPendingOwner sets on RM
  // approval — matched neither filter and rendered in NEITHER table, i.e. it
  // silently vanished from the Owners tab. That is the defect being fixed here.
  // `existingOwners` is deliberately a CATCH-ALL (everything that is not
  // pending_approval) so no current or future enum value can ever make an
  // owner invisible again. Only `pending_approval` stays a named bucket,
  // because it drives its own table and the scroll-into-view effect below.
  const { existingOwners, pendingApprovalOwners } = React.useMemo(() => {
    const existing: IOwnerObject[] = [];
    const pendingApproval: IOwnerObject[] = [];
    owners.forEach((owner) => {
      if (owner.registrationStatus === "pending_approval") {
        pendingApproval.push(owner);
      } else {
        existing.push(owner);
      }
    });
    return { existingOwners: existing, pendingApprovalOwners: pendingApproval };
  }, [owners]);

  // Scroll the pending table into view when arriving with the "pending"
  // filter (dashboard tile deep-link). Dependency on pendingApprovalOwners.length
  // is required because fetchOwners() is async — owners starts as [] and the
  // scroll must fire only after data arrives, not on the empty initial render.
  React.useEffect(() => {
    if (incomingFilter === "pending" && pendingApprovalOwners.length > 0) {
      pendingTableRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [incomingFilter, pendingApprovalOwners.length]);

  // Client-side filtering of the existing-owners bucket BEFORE it reaches
  // DataTable — DataTable does not filter.
  const filteredOwners = React.useMemo(() => {
    const fromNum = balanceFrom.trim() === "" ? null : Number(balanceFrom);
    const toNum = balanceTo.trim() === "" ? null : Number(balanceTo);
    const term = search.trim().toLowerCase();

    return existingOwners.filter((o) => {
      if (withoutEmployees && (o.employees?.length ?? 0) !== 0) return false;

      if (statusFilter === "online" && !o.isOnline) return false;
      if (statusFilter === "active" && !(o.isOnline || (o.onlineEmployeeCount ?? 0) > 0))
        return false;
      if (statusFilter === "pending_review" && !o.hasPendingReview) return false;
      if (statusFilter === "offline" && (o.isOnline || o.hasPendingReview))
        return false;

      if (term) {
        const haystack = `${o.firstName} ${o.lastName} ${o.phone} ${o.country_code}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      const balance = o.balance ?? 0;
      if (fromNum !== null && !Number.isNaN(fromNum) && balance < fromNum)
        return false;
      if (toNum !== null && !Number.isNaN(toNum) && balance > toNum)
        return false;

      return true;
    });
  }, [existingOwners, withoutEmployees, statusFilter, search, balanceFrom, balanceTo]);

  const columns: ColumnDef<IOwnerObject>[] = [
    {
      id: "fullName",
      labelKey: "owners.tableFullName",
      priority: "essential",
      sortable: true,
      render: (row) => `${row.firstName} ${row.lastName}`,
      sortValue: (row) => `${row.firstName} ${row.lastName}`.toLowerCase(),
    },
    {
      id: "status",
      labelKey: "common.status",
      priority: "high",
      sortable: false,
      render: (row) => <StatusChip item={row} />,
    },
    {
      id: "phone",
      labelKey: "owners.tablePhone",
      priority: "high",
      sortable: true,
      render: (row) => `${row.country_code} ${row.phone}`,
      sortValue: (row) => `${row.country_code} ${row.phone}`,
    },
    {
      id: "carWashes",
      labelKey: "owners.colNumberOfWashes",
      priority: "low",
      sortable: true,
      align: "right",
      render: (row) => row.carWashCount ?? row.carWashes?.length ?? 0,
      sortValue: (row) => row.carWashCount ?? row.carWashes?.length ?? 0,
    },
    {
      id: "employees",
      labelKey: "owners.colNumberOfEmployees",
      priority: "low",
      sortable: true,
      align: "right",
      render: (row) => (
        <>
          {row.employees?.length ?? 0}
          {(row.onlineEmployeeCount ?? 0) > 0 && (
            <Typography variant="caption" sx={{ color: "success.main", display: "block" }}>
              {t("owners.activeEmployees", { count: row.onlineEmployeeCount })}
            </Typography>
          )}
        </>
      ),
      sortValue: (row) => row.employees?.length ?? 0,
    },
    {
      id: "balance",
      labelKey: "owners.colBalance",
      priority: "low",
      sortable: true,
      align: "right",
      render: (row) => `${row.balance ?? 0} AZN`,
      sortValue: (row) => row.balance ?? 0,
    },
    {
      id: "pendingReviews",
      labelKey: "manageCarWashes.colPendingReviews",
      priority: "high",
      sortable: true,
      align: "right",
      render: (row) => row.pendingReviewCount ?? 0,
      sortValue: (row) => row.pendingReviewCount ?? 0,
    },
    {
      id: "actions",
      labelKey: "common.actions",
      priority: "essential",
      sortable: false,
      stopRowClick: true,
      render: (row) => (
        <>
          <IconButton
            size="small"
            aria-label={t("common.view")}
            onClick={() => handleClick(row._id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {isSuperAdmin && (
            <IconButton
              size="small"
              aria-label={t("common.delete")}
              onClick={() => handleDeleteClick(row)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const handleCloseModal = () => {
    fetchOwners();
  };

  return (
    <Box className="w-full">
      <Box className="flex justify-between items-center my-5 shadow-md w-full p-2 rounded">
        <Typography variant="h6" gutterBottom>
          <span className="font-bold">{t("owners.title")}</span>
        </Typography>
        <Box>
          <BasicModal
            handleCloseModal={handleCloseModal}
            handleGetOwners={fetchOwners}
          />
        </Box>
      </Box>

      {/* Existing owners table */}
      <Box sx={{ width: "100%", mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Chip
            icon={<PersonAdd />}
            label={t("owners.tabAll", { count: existingOwners.length })}
            color="success"
            size="small"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            size="small"
            label={t("owners.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            size="small"
            select
            label={t("common.status")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">{t("owners.statusAll")}</MenuItem>
            <MenuItem value="online">{t("owners.statusOnline")}</MenuItem>
            <MenuItem value="active">{t("owners.statusActive")}</MenuItem>
            <MenuItem value="pending_review">{t("owners.statusPendingReview")}</MenuItem>
            <MenuItem value="offline">{t("owners.statusOffline")}</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="number"
            label={t("owners.balanceFrom")}
            value={balanceFrom}
            onChange={(e) => setBalanceFrom(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            type="number"
            label={t("owners.balanceTo")}
            value={balanceTo}
            onChange={(e) => setBalanceTo(e.target.value)}
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={withoutEmployees}
                onChange={(e) => setWithoutEmployees(e.target.checked)}
              />
            }
            label={t("owners.withoutEmployees")}
          />
        </Box>

        <DataTable
          tableId="owners"
          columns={columns}
          rows={filteredOwners}
          getRowId={(row) => row._id}
          onRowClick={handleClick}
          emptyLabelKey="owners.noOwnersFound"
          initialOrderBy="fullName"
        />
      </Box>

      <DeleteOwnerDialog
        open={deleteModalOpen}
        owner={ownerToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />

      <Divider sx={{ my: 4 }} />

      {/* Pending owners table — separate from the existing-owners DataTable
          above. pendingReviews (the column) counts open edit requests against
          already-approved owners; this table lists owners not yet approved
          at all. These are not merged. */}
      <Box ref={pendingTableRef} sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Chip
            icon={<Warning />}
            label={t("owners.tabPending", { count: pendingApprovalOwners.length })}
            color="warning"
            size="small"
          />
        </Box>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("owners.tableFullName")}
                  </th>
                  <th style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("owners.tablePhone")}
                  </th>
                  <th style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("owners.tableTimeLeft")}
                  </th>
                  <th style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("owners.tableActions")}
                  </th>
                </tr>
              </thead>
              <TableBody>
                {pendingApprovalOwners.length > 0 ? (
                  pendingApprovalOwners.map((owner) => (
                    <PendingTableRow
                      key={owner._id}
                      item={owner}
                      handleClick={handleClick}
                      onActionComplete={fetchOwners}
                      onError={(msg) => setAlert("error", msg)}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: "20px" }}>
                      <Typography color="text.secondary">
                        {t("owners.noPendingOwners")}
                      </Typography>
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Outlet />
    </Box>
  );
}

const BasicModal = (props: {
  handleCloseModal: () => void;
  handleGetOwners: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button variant="contained" onClick={handleOpen} startIcon={<AddIcon />}>
        {t("owners.addOwner")}
      </Button>

      <OwnerForm
        isOpen={open}
        closeModal={handleClose}
        handleGetOwners={props.handleGetOwners}
      />
    </div>
  );
};
